import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Clock, ChevronRight, Search } from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate, getStatusColor } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyBids() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchBids = async () => {
      try {
        const res = await axios.get(`${API}/bids`);
        setBids(res.data);
      } catch (err) {
        console.error('Failed to fetch bids:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBids();
  }, []);

  const filteredBids = bids.filter(b => 
    b.interest_rate.toString().includes(search) ||
    b.status.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="My Bids">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Bids">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            data-testid="search-bids-input"
            placeholder="Search bids..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white/5 border-white/10"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="px-3 py-1 rounded-full bg-primary/10 text-primary">
            {bids.length} total bids
          </span>
        </div>
      </div>

      {/* Bids List */}
      {filteredBids.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mb-2">No Bids Yet</h3>
            <p className="text-muted-foreground">
              {search ? 'No bids match your search.' : 'Start bidding on loan requests to see them here.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredBids.map((bid, i) => (
            <motion.div
              key={bid.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-primary">{bid.interest_rate}% APR</h3>
                        <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                          {bid.stablecoin || 'USDC'}
                        </span>
                        <Badge className={getStatusColor(bid.status)}>
                          {bid.status}
                        </Badge>
                      </div>
                      {bid.terms && (
                        <p className="text-muted-foreground mb-2">{bid.terms}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(bid.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {bid.status === 'accepted' && (
                        <div className="text-green-500 font-semibold">Won!</div>
                      )}
                      {bid.status === 'rejected' && (
                        <div className="text-red-500 font-semibold">Lost</div>
                      )}
                      {bid.status === 'pending' && (
                        <div className="text-yellow-500 font-semibold">Awaiting Response</div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
